import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, ShieldCheck,
  KeyRound, Coins, Menu, X, Check,
} from "lucide-react";
import { Toaster } from "../components/ui/sonner";
import { toast } from "sonner";
import { api } from "../lib/api";
import { UniverseHero } from "../components/UniverseHero";
import { RippMark } from "../components/RippMark";
import { NftShowcase } from "../components/NftShowcase";
import { usePulse } from "../components/universe/shared";
import { EnterTheCore } from "../components/universe/EnterTheCore";
import { MeetTheAgents } from "../components/universe/MeetTheAgents";
import { WatchRippThink } from "../components/universe/WatchRippThink";
import { ScenarioEngine } from "../components/universe/ScenarioEngine";
import { PositionManagement } from "../components/universe/PositionManagement";
import { DeepBook } from "../components/universe/DeepBook";
import { Backtester } from "../components/universe/Backtester";
import { Analytics } from "../components/universe/Analytics";
import { FinalGate } from "../components/universe/FinalGate";

const IMG = {
  nft: "https://static.prod-images.emergentagent.com/jobs/26786728-e93c-4054-be86-388cf9c50968/images/f383311dc0d35b55d2038e2715427983b60ce36241f5a59f6e51cb3711ac4d98.jpeg",
  coin: "https://static.prod-images.emergentagent.com/jobs/26786728-e93c-4054-be86-388cf9c50968/images/d4a46781c16c7372b88049b0cbfa18de90ffd6bff13c694198b0086bf30bf2be.jpeg",
};

const NAV = [
  { label: "Core", href: "#core" },
  { label: "Agents", href: "#agents" },
  { label: "Think", href: "#think" },
  { label: "Scenarios", href: "#scenarios" },
  { label: "Access", href: "#access" },
  { label: "$RIPP", href: "#token" },
  { label: "Lore", href: "/ripp/lore" },
  { label: "Engine", href: "/ripp/engine" },
  { label: "Roadmap", href: "/ripp/roadmap" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

function PrimaryButton({ children, onClick, testid, className = "" }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`group inline-flex items-center gap-2 rounded-sm bg-[#38BDF8] px-6 py-3 font-mono text-sm font-semibold uppercase tracking-wider text-[#041018] transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_0_28px_rgba(56,189,248,0.45)] ${className}`}
    >
      {children}
    </button>
  );
}

function GhostButton({ children, onClick, testid, className = "" }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-sm border border-white/20 bg-transparent px-6 py-3 font-mono text-sm uppercase tracking-wider text-white transition-colors duration-300 hover:bg-white/5 ${className}`}
    >
      {children}
    </button>
  );
}

function Navbar({ onLaunch }) {
  const [open, setOpen] = useState(false);
  return (
    <header
      data-testid="navbar"
      className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-[#050A14]/70 backdrop-blur-xl"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        <a href="#top" data-testid="brand-logo" className="flex items-center gap-2">
          <RippMark size={30} className="text-[#38BDF8]" glow testid="nav-mark" />
          <span className="font-display text-lg font-extrabold tracking-tight text-white">RIPP<span className="text-[#38BDF8]"> LABS</span></span>
        </a>
        <nav className="hidden items-center gap-8 md:flex">
          {NAV.map((n) => (
            <a key={n.href} href={n.href} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="font-mono text-xs uppercase tracking-widest text-white/60 transition-colors hover:text-[#38BDF8]">
              {n.label}
            </a>
          ))}
          <PrimaryButton testid="nav-launch-app" onClick={onLaunch} className="px-5 py-2.5">
            Launch App <ArrowUpRight className="h-4 w-4" />
          </PrimaryButton>
        </nav>
        <button data-testid="nav-mobile-toggle" className="text-white md:hidden" onClick={() => setOpen((o) => !o)}>
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <div className="border-t border-white/10 bg-[#050A14] px-6 py-4 md:hidden">
          <div className="flex flex-col gap-4">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} onClick={() => setOpen(false)}
                className="font-mono text-xs uppercase tracking-widest text-white/70">{n.label}</a>
            ))}
            <PrimaryButton testid="nav-launch-app-mobile" onClick={onLaunch} className="justify-center">
              Launch App <ArrowUpRight className="h-4 w-4" />
            </PrimaryButton>
          </div>
        </div>
      )}
    </header>
  );
}

function NftAccess({ onLaunch, onMint }) {
  const perks = ["Unlocks full platform access", "Priority strategy slots & compute", "Governance in the Ripp ecosystem", "Lifetime membership, on-chain"];
  return (
    <section id="access" data-testid="nft-access-section" className="relative py-28">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 md:grid-cols-2">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 font-mono text-[11px] uppercase tracking-widest text-[#38BDF8]">
            <KeyRound className="h-3.5 w-3.5" /> Access Pass
          </div>
          <h2 className="font-display text-4xl font-extrabold leading-tight tracking-tighter text-white md:text-5xl">
            The <span className="text-[#38BDF8]">NFT</span> is your key
          </h2>
          <p className="mt-5 max-w-lg font-body text-base font-light leading-relaxed text-white/60">
            Access to the Ripp Labs terminal is gated by a membership NFT. Hold the pass, unlock the agent.
            No subscriptions, no gatekeepers — just verifiable, on-chain access.
          </p>
          <ul className="mt-8 space-y-3">
            {perks.map((p) => (
              <li key={p} className="flex items-center gap-3 font-body text-sm text-white/70">
                <span className="grid h-5 w-5 place-items-center rounded-full bg-[#38BDF8]/15 text-[#38BDF8]"><Check className="h-3 w-3" /></span>
                {p}
              </li>
            ))}
          </ul>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <PrimaryButton testid="access-mint-pass" onClick={onMint}>Mint Access Pass <KeyRound className="h-4 w-4" /></PrimaryButton>
            <GhostButton testid="access-launch-app" onClick={onLaunch}>Enter Terminal <ArrowRight className="h-4 w-4" /></GhostButton>
          </div>
        </motion.div>

        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1}
          className="relative">
          <div className="absolute inset-0 -z-10 mx-auto h-3/4 w-3/4 rounded-full bg-[#38BDF8]/10 blur-3xl" aria-hidden="true" />
          <NftShowcase testid="access-nft-showcase" />
        </motion.div>
      </div>
    </section>
  );
}

function TokenMarquee() {
  const row = Array.from({ length: 10 });
  return (
    <div className="relative overflow-hidden py-4">
      <div className="ripp-marquee-track">
        {row.map((_, i) => (
          <span key={i} className="mx-4 font-display text-6xl font-black tracking-tighter text-white/5 md:text-8xl">
            $RIPP <span className="text-[#38BDF8]/10">•</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function Tokenomics() {
  const utility = [
    { icon: KeyRound, t: "Access & Utility", d: "Stake $RIPP to unlock premium strategy modules and compute." },
    { icon: Coins, t: "Ecosystem Rewards", d: "Earn from performance pools and ecosystem incentives." },
    { icon: ShieldCheck, t: "Governance", d: "Vote on new strategies, risk defaults and roadmap." },
  ];
  return (
    <section id="token" data-testid="tokenomics-section" className="relative border-t border-white/10 bg-[#080808] py-28">
      <TokenMarquee />
      <div className="mx-auto mt-6 grid max-w-7xl grid-cols-1 items-center gap-14 px-6 md:grid-cols-2">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="relative order-2 md:order-1">
          <div className="absolute inset-0 -z-10 mx-auto h-2/3 w-2/3 rounded-full bg-[#38BDF8]/10 blur-3xl" aria-hidden="true" />
          <img src={IMG.coin} alt="$RIPP token" className="ripp-recolor mx-auto w-full max-w-md object-contain drop-shadow-[0_0_40px_rgba(56,189,248,0.15)]" />
        </motion.div>
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1} className="order-1 md:order-2">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 font-mono text-[11px] uppercase tracking-widest text-[#38BDF8]">
            <Coins className="h-3.5 w-3.5" /> The Ecosystem
          </div>
          <h2 className="font-display text-4xl font-extrabold leading-tight tracking-tighter text-white md:text-5xl">
            Powered by <span className="text-[#38BDF8]">$RIPP</span>
          </h2>
          <p className="mt-5 max-w-lg font-body text-base font-light leading-relaxed text-white/60">
            $RIPP is the fuel of the Ripp Labs ecosystem — utility, rewards and governance in one token.
          </p>
          <div className="mt-8 space-y-4">
            {utility.map((u) => (
              <div key={u.t} className="flex items-start gap-4 rounded-lg border border-white/10 bg-[#0B1220] p-5">
                <div className="rounded-sm bg-[#38BDF8]/10 p-2.5 text-[#38BDF8]"><u.icon className="h-5 w-5" /></div>
                <div>
                  <div className="font-display text-base font-bold text-white">{u.t}</div>
                  <div className="mt-1 font-body text-sm font-light text-white/55">{u.d}</div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Waitlist({ scrollId }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await api.waitlist(email);
      setDone(true);
      toast.success("You're on the list. Welcome to Ripp Labs.", { className: "font-mono text-xs" });
      setEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong", { className: "font-mono text-xs" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id={scrollId} data-testid="waitlist-section" className="relative py-28">
      <div className="mx-auto max-w-3xl px-6 text-center">
        <SectionHeader center kicker="03 — Get in" title="Join the waitlist" />
        <p className="mx-auto mt-4 max-w-xl font-body text-base font-light text-white/55">
          Early access to the terminal, the NFT pass mint and the $RIPP ecosystem drops.
        </p>
        <form onSubmit={submit} data-testid="waitlist-form" className="mx-auto mt-10 flex max-w-md flex-col gap-3 sm:flex-row">
          <input
            data-testid="waitlist-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@domain.xyz"
            className="w-full rounded-sm border border-white/15 bg-[#0B1220] px-4 py-3 font-mono text-sm text-white placeholder:text-white/30 outline-none transition-colors focus:border-[#38BDF8]/60"
          />
          <PrimaryButton testid="waitlist-submit" className="justify-center whitespace-nowrap">
            {done ? "Joined" : loading ? "..." : "Notify Me"}
            {!loading && !done && <ArrowRight className="h-4 w-4" />}
            {done && <Check className="h-4 w-4" />}
          </PrimaryButton>
        </form>
      </div>
    </section>
  );
}

function Footer({ onLaunch }) {
  return (
    <footer data-testid="footer-section" className="relative overflow-hidden border-t border-white/10 bg-[#050A14] pt-20">
      <div className="mx-auto max-w-7xl px-6">
        <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
          <div>
            <div className="font-display text-lg font-extrabold tracking-tight text-white">RIPP<span className="text-[#38BDF8]"> LABS</span></div>
            <p className="mt-2 max-w-sm font-body text-sm font-light text-white/40">Build your strategy and let it RIPP.</p>
          </div>
          <div className="flex flex-wrap gap-6">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} className="font-mono text-xs uppercase tracking-widest text-white/40 transition-colors hover:text-[#38BDF8]">{n.label}</a>
            ))}
            <button onClick={onLaunch} data-testid="footer-launch-app" className="font-mono text-xs uppercase tracking-widest text-[#38BDF8]">Launch App ↗</button>
          </div>
        </div>
      </div>
      <div className="mt-16 select-none px-6 text-center leading-none">
        <span className="font-display text-[18vw] font-black tracking-tighter text-white/[0.04]">RIPP.XYZ</span>
      </div>
      <div className="border-t border-white/5 py-6 text-center font-mono text-[11px] uppercase tracking-widest text-white/25">
        © {new Date().getFullYear()} Ripp Labs — Trading involves risk.
      </div>
    </footer>
  );
}

function SectionHeader({ kicker, title, center }) {
  return (
    <div className={center ? "text-center" : ""}>
      <div className={`mb-4 font-mono text-xs uppercase tracking-widest text-[#38BDF8] ${center ? "" : ""}`}>{kicker}</div>
      <h2 className="font-display text-3xl font-extrabold leading-tight tracking-tighter text-white md:text-5xl">{title}</h2>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const pulse = usePulse();
  const launch = () => navigate("/app");
  const mint = () => navigate("/mint");
  const scrollWaitlist = () => {
    const el = document.getElementById("waitlist");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <div className="ripp-noise min-h-screen bg-[#050A14] font-body text-white antialiased">
      <Navbar onLaunch={launch} />
      <main className="relative z-10">
        <UniverseHero />
        <EnterTheCore pulse={pulse} />
        <MeetTheAgents pulse={pulse} />
        <WatchRippThink pulse={pulse} />
        <ScenarioEngine pulse={pulse} />
        <PositionManagement pulse={pulse} />
        <DeepBook pulse={pulse} />
        <Backtester pulse={pulse} />
        <Analytics pulse={pulse} />
        <FinalGate onMint={mint} onWaitlist={scrollWaitlist} />
        <NftAccess onLaunch={launch} onMint={mint} />
        <Tokenomics />
        <Waitlist scrollId="waitlist" />
      </main>
      <Footer onLaunch={launch} />
      <Toaster position="bottom-right" theme="dark" />
    </div>
  );
}
